import moment from 'moment';

import { translateString } from '@src/util/intl/translateString';

import {
  GROUP_BY_DEFAULT,
  ONE_WEEK_BACK,
  RELATIVE_DATE_PER_DEFAULT,
  RELATIVE_OPTIONS,
  TODAY_DATE,
} from './components/ChartFilters/constants';

export const FIELD_FORM_IDS = {
  RELATIVE: 'relative',
  DATE_RANGE: 'dateRange',
  GROUP_BY: 'groupBy',
};

function toBoardChartDate(value) {
  if (value == null) return null;
  if (typeof value.toDate === 'function') {
    const date = value.toDate();
    return date instanceof Date && !Number.isNaN(date.getTime()) ? date : null;
  }
  const date = value instanceof Date ? value : moment(value).toDate();
  return date instanceof Date && !Number.isNaN(date.getTime()) ? date : null;
}

export function resolveChartFiltersFromBoard(boardSelected) {
  const interval = boardSelected?.queryParams?.interval;
  if (!interval) return null;

  const range = boardSelected.queryParams.range ?? {};
  const { dateRange, isRelative, relativeAmount, dateType } = range;
  let resolvedDateRange = [ONE_WEEK_BACK, TODAY_DATE];
  if (Array.isArray(dateRange) && dateRange.length === 2) {
    const start = toBoardChartDate(dateRange[0]);
    const end = toBoardChartDate(dateRange[1]);
    if (start && end) {
      resolvedDateRange = [start, end];
    }
  }

  return {
    relative: { isRelative, dateType, relativeAmount },
    dateRange: resolvedDateRange,
    groupBy: interval,
  };
}

export function normalizeStoredChartFilters(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const rel = raw.relative;
  if (!rel || typeof rel !== 'object' || !('isRelative' in rel)) return null;

  let dateRange = raw.dateRange;
  if (Array.isArray(dateRange) && dateRange.length === 2) {
    dateRange = [moment(dateRange[0]), moment(dateRange[1])];
  } else {
    dateRange = [moment(ONE_WEEK_BACK), moment(TODAY_DATE)];
  }

  return {
    relative: { ...RELATIVE_DATE_PER_DEFAULT, ...rel },
    dateRange,
    groupBy: raw.groupBy ?? GROUP_BY_DEFAULT,
  };
}

export function filterValuesToChartFilters(values) {
  const relEntry = values[FIELD_FORM_IDS.RELATIVE];
  const drEntry = values[FIELD_FORM_IDS.DATE_RANGE];
  const gbEntry = values[FIELD_FORM_IDS.GROUP_BY];

  const rawRelative = relEntry?.value;
  let relative = rawRelative ?? RELATIVE_DATE_PER_DEFAULT;
  if (rawRelative === null) {
    relative = { ...RELATIVE_DATE_PER_DEFAULT, isRelative: false };
  } else if (relative && typeof relative === 'object' && !Array.isArray(relative)) {
    relative = { ...RELATIVE_DATE_PER_DEFAULT, ...relative };
  } else {
    relative = RELATIVE_DATE_PER_DEFAULT;
  }

  let dateRange = drEntry?.value;
  if (!Array.isArray(dateRange) || dateRange.length !== 2) {
    dateRange = [moment(ONE_WEEK_BACK), moment(TODAY_DATE)];
  } else {
    dateRange = [moment(dateRange[0]), moment(dateRange[1])];
  }

  const rawGroupBy = gbEntry?.value;
  const groupBy = rawGroupBy != null && rawGroupBy !== '' ? rawGroupBy : GROUP_BY_DEFAULT;

  const isRelative = relative.isRelative === true || relative.isRelative === 'true';

  return {
    relative: { ...relative, isRelative },
    dateRange,
    groupBy,
  };
}

export function filterValuesToChartFiltersUi(values, fallback) {
  if (!values || Object.keys(values).length === 0) {
    return fallback;
  }

  return {
    relative:
      values[FIELD_FORM_IDS.RELATIVE]?.value !== undefined
        ? values[FIELD_FORM_IDS.RELATIVE]?.value
        : fallback.relative,
    dateRange:
      values[FIELD_FORM_IDS.DATE_RANGE]?.value !== undefined
        ? values[FIELD_FORM_IDS.DATE_RANGE]?.value
        : fallback.dateRange,
    groupBy:
      values[FIELD_FORM_IDS.GROUP_BY]?.value !== undefined
        ? values[FIELD_FORM_IDS.GROUP_BY]?.value
        : fallback.groupBy,
  };
}

export function formatGroupByTag(groupBy, intl) {
  if (groupBy == null || groupBy === '') return '';
  return intl.formatMessage({ id: `dateTypes.${groupBy}` }, { amount: 1 });
}

export function formatRelativeForTag(relative, intl) {
  if (relative == null || typeof relative !== 'object') return '';
  if (!relative.isRelative) {
    return intl.formatMessage({ id: 'dashboards.schedules.absolute' });
  }
  const opt = RELATIVE_OPTIONS.find(o => o.value === relative.relativeAmount);
  const period = opt ? opt.label : String(relative.relativeAmount ?? '');
  return `${intl.formatMessage({ id: 'dashboards.schedules.relative' })} · ${period}`;
}

export function formatDateRangeForTag(dateRange, intl) {
  if (!Array.isArray(dateRange) || dateRange.length !== 2) return '';
  const a = moment(dateRange[0]);
  const b = moment(dateRange[1]);
  return `${a.format('L')} – ${b.format('L')}`;
}

export function buildInitMetricsFilterValues(chartFilters, intl) {
  const { relative, dateRange, groupBy } = chartFilters;
  const isRelativeMode = relative?.isRelative === true || relative?.isRelative === 'true';

  return {
    [FIELD_FORM_IDS.RELATIVE]: {
      value: isRelativeMode ? relative : null,
      label: translateString('dashboards.chart.creation.date'),
      valueTranslated: isRelativeMode ? formatRelativeForTag(relative, intl) : '',
      valueType: 'string',
      clearValue: null,
    },
    [FIELD_FORM_IDS.DATE_RANGE]: {
      value: isRelativeMode ? null : dateRange,
      label: translateString('dashboards.chart.creation.date'),
      valueTranslated: isRelativeMode ? '' : formatDateRangeForTag(dateRange, intl),
      valueType: 'array',
      clearValue: null,
    },
    [FIELD_FORM_IDS.GROUP_BY]: {
      value: groupBy,
      label: translateString('dashboards.chart.group.by'),
      valueTranslated: formatGroupByTag(groupBy, intl),
      valueType: 'string',
      clearValue: null,
    },
  };
}

export function chartFiltersToStoragePayload(chart) {
  return {
    relative: chart.relative,
    dateRange: chart.dateRange.map(d =>
      moment.isMoment(d) ? d.toISOString() : moment(d).toISOString(),
    ),
    groupBy: chart.groupBy,
  };
}

export function buildChartFilterClearProps(key) {
  switch (key) {
    case FIELD_FORM_IDS.RELATIVE:
      return {
        key,
        value: null,
        label: translateString('dashboards.chart.creation.date'),
        valueTranslated: '',
        valueType: 'string',
        clearValue: null,
      };
    case FIELD_FORM_IDS.DATE_RANGE:
      return {
        key,
        value: null,
        label: translateString('dashboards.chart.creation.date'),
        valueTranslated: '',
        valueType: 'array',
        clearValue: null,
      };
    case FIELD_FORM_IDS.GROUP_BY:
      return {
        key,
        value: null,
        label: translateString('dashboards.chart.group.by'),
        valueTranslated: '',
        valueType: 'string',
        clearValue: null,
      };
    default:
      return { key, value: null, valueType: 'string', clearValue: null };
  }
}

export function buildChartFilterSetValueProps(key, value, intl) {
  switch (key) {
    case FIELD_FORM_IDS.RELATIVE:
      return {
        key,
        value,
        label: translateString('dashboards.chart.creation.date'),
        valueTranslated: formatRelativeForTag(value, intl),
        valueType: 'string',
        clearValue: null,
      };
    case FIELD_FORM_IDS.DATE_RANGE:
      return {
        key,
        value,
        label: translateString('dashboards.chart.creation.date'),
        valueTranslated: formatDateRangeForTag(value, intl),
        valueType: 'array',
        clearValue: null,
      };
    case FIELD_FORM_IDS.GROUP_BY:
      return {
        key,
        value,
        label: translateString('dashboards.chart.group.by'),
        valueTranslated: formatGroupByTag(value, intl),
        valueType: 'string',
        clearValue: null,
      };
    default:
      return { key, value, valueType: 'string' };
  }
}
